import React, { useCallback, useEffect, useMemo, useState } from "react";
import ListPagination, {
  LIST_DEFAULT_PAGE_SIZE,
  buildPageQuery,
  createPageChangeHandler,
  parseListFromPayload,
  parseTotalFromPayload,
} from "../../components/ListPagination";
import {
  Table,
  Select,
  DatePicker,
  Switch,
  Spin,
  Space,
  message,
} from "antd";
import moment from "moment";
import dayjs from "dayjs";
import API, { WORLD_CUP_SCHEDULES, SYNC_MATCHES } from "../../network/api";
import { TEXT_DEF } from "../../constant/text";
import {
  formatMatchDateAndKickoff,
  formatMomentDateDashTime,
} from "../../utils/formatDateDashTime";

const PHASE = {
  UPCOMING: "upcoming",
  ONGOING: "ongoing",
  FINISHED: "finished",
};

const TEXT = {
  statusFilter: "Trạng thái",
  fromDate: "Từ ngày",
  toDate: "Đến ngày",
  allowDisplayFilter: "Cho phép hiển thị",
  colIndex: "#",
  colMatch: "Trận đấu",
  colKickoff: "Thời gian thi đấu",
  colStatus: "Trạng thái",
  colAllowDisplay: "Cho phép hiển thị",
  phaseUpcoming: "Sắp diễn ra",
  phaseOngoing: "Đang diễn ra",
  phaseFinished: "Đã diễn ra",
  allowYes: "Có",
  allowNo: "Không",
  allowAll: "Tất cả",
  toggleOk: "Đã cập nhật hiển thị trận.",
  toggleFail: "Không thể cập nhật hiển thị.",
};

function normalizeStatusText(raw) {
  return String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[đ]/g, "d")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function parsePhaseFromStatusText(rawStatus) {
  const s = normalizeStatusText(rawStatus);
  if (!s) return null;
  if (
    s.includes("sap dien ra") ||
    s.includes("chua dien ra") ||
    s.includes("upcoming")
  ) {
    return PHASE.UPCOMING;
  }
  if (s.includes("dang dien ra") || s.includes("ongoing") || s.includes("live")) {
    return PHASE.ONGOING;
  }
  if (
    s.includes("da ket thuc") ||
    s.includes("da dien ra") ||
    s === "ended" ||
    s === "end" ||
    s.includes("finished") ||
    s.includes("completed")
  ) {
    return PHASE.FINISHED;
  }
  return null;
}

function phaseLabel(phase) {
  if (phase === PHASE.FINISHED) return TEXT.phaseFinished;
  if (phase === PHASE.ONGOING) return TEXT.phaseOngoing;
  return TEXT.phaseUpcoming;
}

function formatKickoff(val) {
  if (val == null || val === "") return "—";
  let m = moment(val);
  if (!m.isValid() && (typeof val === "number" || /^\d+$/.test(String(val)))) {
    const n = Number(val);
    m = moment(n < 1e12 ? n * 1000 : n);
  }
  if (m.isValid()) return formatMomentDateDashTime(m);
  const loose = moment(val);
  return loose.isValid() ? formatMomentDateDashTime(loose) : "—";
}

/** Thời gian thi đấu — `matchDate` + `kickoff` ("08:00") → `DD/MM - HH:mm`. */
function formatMatchKickoffDisplay(row) {
  const fromParts = formatMatchDateAndKickoff(row?.matchDate, row?.kickoff);
  if (fromParts !== "—") return fromParts;
  const m = resolveKickoffMoment(row);
  if (m && m.isValid()) return formatMomentDateDashTime(m);
  return formatKickoff(row?.matchAt ?? row?.matchDate);
}

/** Moment để lọc ngày / suy trạng thái trận từ `matchDate` + `kickoff` hoặc `matchAt`. */
function resolveKickoffMoment(row) {
  const matchDate = row?.matchDate;
  const kickoff = row?.kickoff != null ? String(row.kickoff).trim() : "";
  if (matchDate && kickoff) {
    const timePart = kickoff.match(/(\d{1,2}:\d{2})/);
    if (timePart) {
      const combined = moment(
        `${matchDate} ${timePart[1]}`,
        "YYYY-MM-DD HH:mm",
        true
      );
      if (combined.isValid()) return combined;
    }
    const dm = kickoff.match(/^(\d{1,2})\/(\d{1,2})\s+(\d{1,2}:\d{2})/);
    if (dm) {
      const y = moment(matchDate, "YYYY-MM-DD", true).isValid()
        ? moment(matchDate, "YYYY-MM-DD").year()
        : moment().year();
      const parsed = moment(
        `${dm[1]}/${dm[2]}/${y} ${dm[3]}`,
        "DD/MM/YYYY HH:mm",
        true
      );
      if (parsed.isValid()) return parsed;
    }
  }

  const matchAt = row?.matchAt;
  if (matchAt != null && matchAt !== "") {
    const m = moment(matchAt);
    if (m.isValid()) return m;
  }

  if (matchDate) {
    const d = moment(matchDate, "YYYY-MM-DD", true);
    if (d.isValid()) return d;
  }

  return null;
}

function mapApiItem(item, index) {
  const id = String(item?.matchId ?? item?.id ?? item?._id ?? index);
  const homeTeam = item?.homeTeam ?? item?.homeName ?? "";
  const awayTeam = item?.awayTeam ?? item?.awayName ?? "";
  const label =
    item?.matchLabel ||
    item?.label ||
    (homeTeam && awayTeam ? `${homeTeam} vs ${awayTeam}` : `Trận ${id}`);
  const rawStatus = item?.status ?? item?.confirmStatus;
  const statusText =
    rawStatus != null && String(rawStatus).trim() !== ""
      ? String(rawStatus).trim()
      : "";
  const statusPhase = parsePhaseFromStatusText(statusText);
  let matchDate = "";
  if (item?.matchDate != null && item.matchDate !== "") {
    matchDate =
      typeof item.matchDate === "string"
        ? item.matchDate
        : moment(item.matchDate).isValid()
          ? moment(item.matchDate).format("YYYY-MM-DD")
          : String(item.matchDate);
  }
  const matchAt =
    item?.matchAt ||
    item?.matchDate ||
    item?.scheduledAt ||
    item?.date ||
    null;
  if (!matchDate && matchAt != null && matchAt !== "") {
    const m = moment(matchAt);
    matchDate = m.isValid() ? m.format("YYYY-MM-DD") : "";
  }
  const hs =
    item?.homeScore != null
      ? Number(item.homeScore)
      : item?.scoreHome != null
        ? Number(item.scoreHome)
        : null;
  const as =
    item?.awayScore != null
      ? Number(item.awayScore)
      : item?.scoreAway != null
        ? Number(item.scoreAway)
        : null;
  return {
    id,
    homeTeam,
    awayTeam,
    matchDate,
    kickoff: item?.kickoff != null ? String(item.kickoff).trim() : "",
    group: item?.group != null ? String(item.group) : "",
    matchLabel: label,
    homeScore: hs != null && !Number.isNaN(hs) ? hs : null,
    awayScore: as != null && !Number.isNaN(as) ? as : null,
    matchAt,
    statusText,
    statusPhase,
    isAllowed: pickIsAllowed(item),
  };
}

function toPayloadMatchId(id) {
  if (id == null || id === "") return id;
  const n = Number(id);
  return Number.isFinite(n) ? n : id;
}

/** Chỉ `true` (hoặc tương đương từ BE) mới coi là «Có» / bật Switch. */
function pickIsAllowed(item) {
  const v = item?.isAllowed ?? item?.displayAllowed;
  if (v === true || v === "true" || Number(v) === 1) return true;
  return false;
}

/** Body một trận cho POST `asean-cup/admin/sync-matches`. */
function buildSyncMatchEntry(record, isAllowed) {
  const matchId = toPayloadMatchId(record.id);
  return {
    matchId: typeof matchId === "number" ? matchId : Number(matchId) || 0,
    homeTeam: record.homeTeam ?? "",
    awayTeam: record.awayTeam ?? "",
    matchDate: record.matchDate ?? "",
    kickoff: record.kickoff ?? "",
    group: record.group ?? "",
    isAllowed: !!isAllowed,
  };
}

const MatchDisplaySettingsTab = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [phaseFilter, setPhaseFilter] = useState(undefined);
  const [allowFilter, setAllowFilter] = useState("all");
  const [fromDate, setFromDate] = useState(() => dayjs().startOf("day"));
  const [toDate, setToDate] = useState(null);
  const [toggleBusyId, setToggleBusyId] = useState(null);
  const [listPage, setListPage] = useState(1);
  const [listPageSize, setListPageSize] = useState(LIST_DEFAULT_PAGE_SIZE);
  const [totalRecords, setTotalRecords] = useState(0);

  const loadList = useCallback(async () => {
    if (!fromDate || !fromDate.isValid?.()) return;
    setLoading(true);
    try {
      const { query: pageQuery } = buildPageQuery(listPage, listPageSize);
      const parts = [`fromDate=${fromDate.format("DD-MM-YYYY")}`, pageQuery];
      if (toDate && toDate.isValid?.()) {
        parts.push(`toDate=${toDate.format("DD-MM-YYYY")}`);
      }
      if (allowFilter === "yes") parts.push("isAllowed=true");
      else if (allowFilter === "no") parts.push("isAllowed=false");
      if (phaseFilter) parts.push(`phase=${phaseFilter}`);
      const url = `${WORLD_CUP_SCHEDULES}?${parts.join("&")}`;
      const res = await API.get(url);
      const payload = res.data?.data ?? res.data;
      const list = parseListFromPayload(payload);
      setRows(list.map((item, i) => mapApiItem(item, i)));
      setTotalRecords(parseTotalFromPayload(payload, list.length));
    } catch (err) {
      console.warn(err);
      setRows([]);
      setTotalRecords(0);
      messageApi.error(
        err?.response?.data?.message || "Không tải được lịch trận đấu."
      );
    } finally {
      setLoading(false);
    }
  }, [
    fromDate,
    toDate,
    listPage,
    listPageSize,
    phaseFilter,
    allowFilter,
    messageApi,
  ]);

  useEffect(() => {
    setListPage(1);
  }, [phaseFilter, allowFilter, fromDate, toDate]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  /** Lọc phase phía FE khi BE chưa lọc theo query `phase` */
  const displayRows = useMemo(() => {
    if (!phaseFilter) return rows;
    return rows.filter((row) => row.statusPhase === phaseFilter);
  }, [rows, phaseFilter]);

  const onListPageChange = createPageChangeHandler(
    listPage,
    listPageSize,
    setListPage,
    setListPageSize
  );

  const onToggleAllowed = async (record, checked) => {
    const prev = record.isAllowed;
    const rowId = record.id;
    setRows((list) =>
      list.map((x) => (x.id === rowId ? { ...x, isAllowed: checked } : x))
    );
    setToggleBusyId(rowId);
    try {
      await API.post(SYNC_MATCHES, {
        matches: [buildSyncMatchEntry(record, checked)],
      });
      messageApi.success(TEXT.toggleOk);
    } catch (e) {
      console.warn(e);
      setRows((list) =>
        list.map((x) => (x.id === rowId ? { ...x, isAllowed: prev } : x))
      );
      messageApi.error(e?.response?.data?.message || TEXT.toggleFail);
    } finally {
      setToggleBusyId(null);
    }
  };

  const columns = [
    {
      title: TEXT.colIndex,
      key: "idx",
      width: 56,
      align: "center",
      render: (_, __, index) => (listPage - 1) * listPageSize + index + 1,
    },
    {
      title: TEXT.colMatch,
      dataIndex: "matchLabel",
      key: "matchLabel",
      align: "center",
    },
    {
      title: TEXT.colKickoff,
      key: "kickoff",
      align: "center",
      render: (_, record) => formatMatchKickoffDisplay(record),
    },
    {
      title: TEXT.colStatus,
      key: "statusText",
      align: "center",
      render: (_, record) =>
        record.statusPhase ? phaseLabel(record.statusPhase) : record.statusText || "—",
    },
    {
      title: TEXT.colAllowDisplay,
      key: "allow",
      align: "center",
      width: 140,
      render: (_, record) => (
        <Switch
          checkedChildren={TEXT.allowYes}
          unCheckedChildren={TEXT.allowNo}
          checked={record.isAllowed === true}
          loading={toggleBusyId === record.id}
          onChange={(checked) => onToggleAllowed(record, checked)}
        />
      ),
    },
  ];

  return (
    <div className="col-12" style={{ padding: "8px 0", width: "100%" }}>
      {contextHolder}
      <Space
        wrap
        style={{
          marginBottom: 16,
          width: "100%",
          alignItems: "flex-end",
        }}
      >
        <div>
          <div style={{ marginBottom: 4, fontWeight: 500 }}>
            {TEXT.statusFilter}
          </div>
          <Select
            allowClear
            placeholder="Chọn trạng thái"
            style={{ width: 180 }}
            value={phaseFilter}
            onChange={setPhaseFilter}
            options={[
              { value: PHASE.UPCOMING, label: TEXT.phaseUpcoming },
              { value: PHASE.ONGOING, label: TEXT.phaseOngoing },
              { value: PHASE.FINISHED, label: TEXT.phaseFinished },
            ]}
          />
        </div>
        <div>
          <div style={{ marginBottom: 4, fontWeight: 500 }}>{TEXT.fromDate}</div>
          <DatePicker
            allowClear={false}
            format="DD-MM-YYYY"
            value={fromDate}
            onChange={(d) => setFromDate(d ? d.startOf("day") : dayjs().startOf("day"))}
            placeholder="DD-MM-YYYY"
            style={{ width: 160 }}
          />
        </div>
        <div>
          <div style={{ marginBottom: 4, fontWeight: 500 }}>{TEXT.toDate}</div>
          <DatePicker
            format="DD-MM-YYYY"
            value={toDate}
            onChange={(d) => setToDate(d ? d.endOf("day") : null)}
            placeholder="DD-MM-YYYY"
            style={{ width: 160 }}
            disabledDate={(d) =>
              !!fromDate && !!d && d.startOf("day").isBefore(fromDate.startOf("day"))
            }
          />
        </div>
        <div>
          <div style={{ marginBottom: 4, fontWeight: 500 }}>
            {TEXT.allowDisplayFilter}
          </div>
          <Select
            style={{ width: 140 }}
            value={allowFilter}
            onChange={(v) => setAllowFilter(v ?? "all")}
            options={[
              { value: "all", label: TEXT.allowAll },
              { value: "yes", label: TEXT.allowYes },
              { value: "no", label: TEXT.allowNo },
            ]}
          />
        </div>
      </Space>

      <Spin spinning={loading}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={displayRows}
          pagination={false}
          bordered
          locale={{ emptyText: TEXT_DEF.text0035 }}
          scroll={{ x: true }}
        />
        <ListPagination
          current={listPage}
          total={totalRecords}
          pageSize={listPageSize}
          onChange={onListPageChange}
          unit="trận đấu"
        />
      </Spin>
    </div>
  );
};

export default MatchDisplaySettingsTab;
